import { CSSProperties } from "react";
import { useInstructorColor } from "context/InstructorColorProvider";
import { colors } from "shared/theme/color";
import { ClassCategory } from "shared/types/enum";
import { seatsLine } from "./CalendarBlock";
import { CalendarSchedule } from "./types";
import { formatToLocalDateRange } from "./utils";
import { textPx } from "shared/constants/zoom";

const truncate: CSSProperties = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

/**
 * Full-size schedule block for pop-ups: class name and instructor on the left,
 * time on the right, in the instructor's colours with the same left bar as the
 * calendar blocks. Always this one size, never scaled by the class's length.
 * Shared by the Aksi, clash-warning and "+N" pop-ups so they stay one block.
 */
export const PopupScheduleBlock = <T extends CalendarSchedule>({
  event,
  onClick,
}: {
  event: T;
  onClick?: (event: T) => void;
}) => {
  const { getInstructorColorById } = useInstructorColor();
  const found = getInstructorColorById(event.instructor_id ?? event.staff_id);
  const colour = !event.is_cancelled && found ? found : null;
  const strike = event.is_cancelled ? "line-through" : undefined;
  // An open class shows its seats ("3/12"), like its calendar block; any
  // other class names its clients in full (the pop-up has the room).
  const detail =
    event.class_category === ClassCategory.Open
      ? seatsLine(event)
      : event.customer_names?.length
      ? event.customer_names.join(", ")
      : null;
  const interactive = !!onClick;

  return (
    <div
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={interactive ? () => onClick(event) : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick(event);
              }
            }
          : undefined
      }
      className="wc-popup-block"
      style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
        padding: "10px 13px 10px 16px",
        borderRadius: 10,
        // A flex item with overflow hidden may shrink to nothing, so in a
        // height-capped list (the +N pop-up) the cards squashed instead of
        // the list scrolling. Each card keeps its own height.
        flexShrink: 0,
        overflow: "hidden",
        background: colour ? colour.bg : colors.bgSoft,
        color: colour ? colour.text : colors.ink2,
        cursor: interactive ? "pointer" : "default",
      }}
    >
      <span
        aria-hidden
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 3,
          background: colour ? colour.accent : colors.ink3,
        }}
      />
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontWeight: 600,
            fontSize: textPx(13),
            lineHeight: 1.25,
            textDecoration: strike,
            ...truncate,
          }}
        >
          {event.class_name ?? event.title}
        </div>
        <div
          style={{
            fontSize: textPx(12),
            marginTop: 3,
            textDecoration: strike,
            ...truncate,
          }}
        >
          {event.instructor_name}
          {detail ? <> · {detail}</> : null}
        </div>
      </div>
      <span
        style={{
          fontSize: textPx(12),
          fontWeight: 600,
          whiteSpace: "nowrap",
          flexShrink: 0,
          textDecoration: strike,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {formatToLocalDateRange(event.start_time, event.end_time)}
      </span>
    </div>
  );
};
